/**
 * Bulk promote — how many of the selected elements actually fit.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Capacity is per POOL, so "cap is 8" is never one number: selecting six rows
 * spread over two pools can be three promotions and three refusals. This walks
 * the selection in the order it is drawn on screen and spends each pool's
 * headroom until it runs out — so the elements that go live are the ones the
 * user sees at the top, not whichever request happened to land first.
 *
 * Overflow is REPORTED, never resolved. Evicting live elements to make room
 * for a multi-selection is the one behaviour nobody would predict from ticking
 * boxes, so the caller sends `allowReplace: false` and the server refuses too.
 *
 * A pool the listing doesn't describe (or an uncapped one) is treated as open
 * and attempted: the server knows the real cap and refuses precisely, which
 * beats silently dropping a row on a guess.
 */

import type { QueueElement, QueuePoolSummary } from '@trafficpulse/shared';

export interface BulkPoolPlan {
  poolId: string;
  poolName: string;
  cap: number | null;
  active: number;
  /** How many of the selection sit in this pool. */
  selected: number;
  /** How many of them have a slot. */
  fits: number;
}

export interface BulkPromotePlan {
  /** Selected elements with a slot, in on-screen order. */
  fitting: QueueElement[];
  /** Selected elements with no slot — not attempted. */
  overflow: QueueElement[];
  /** Only the pools the selection touches, in first-seen order. */
  pools: BulkPoolPlan[];
  /** Slots that would have to exist to promote everything selected. */
  shortfall: number;
}

function headroomOf(pool: QueuePoolSummary | undefined): number {
  if (!pool || pool.cap === null || pool.cap === undefined) return Infinity;
  return Math.max(0, pool.cap - pool.active);
}

/**
 * @param elements selected elements, already in the order the table draws them
 * @param pools    pool summaries from the queue listing
 */
export function planBulkPromote(
  elements: QueueElement[],
  pools: QueuePoolSummary[],
): BulkPromotePlan {
  const byId = new Map(pools.map((p) => [p.poolId, p]));
  const room = new Map<string, number>();
  const plans = new Map<string, BulkPoolPlan>();
  const fitting: QueueElement[] = [];
  const overflow: QueueElement[] = [];

  for (const el of elements) {
    const summary = byId.get(el.poolId);
    if (!room.has(el.poolId)) room.set(el.poolId, headroomOf(summary));
    if (!plans.has(el.poolId)) {
      plans.set(el.poolId, {
        poolId: el.poolId,
        poolName: summary?.poolName ?? el.poolName,
        cap: summary?.cap ?? null,
        active: summary?.active ?? 0,
        selected: 0,
        fits: 0,
      });
    }
    const plan = plans.get(el.poolId)!;
    plan.selected += 1;
    const left = room.get(el.poolId)!;
    if (left > 0) {
      room.set(el.poolId, left - 1);
      plan.fits += 1;
      fitting.push(el);
    } else {
      overflow.push(el);
    }
  }

  return {
    fitting,
    overflow,
    pools: [...plans.values()],
    shortfall: overflow.length,
  };
}
